import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Loader2, Download, FileText, Camera, 
  Calendar, User, Car, Clock, DollarSign, Shield,
  CheckCircle, AlertTriangle, XCircle, Image as ImageIcon,
  ChevronRight, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminBookingEvidence - Admin page for reviewing booking dispute evidence
 * Shows: Booking details, agreement PDF, pre/post inspection photos, timeline
 */
const AdminBookingEvidence = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const { api, user, isPlatformOwner } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [evidence, setEvidence] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (!isPlatformOwner?.() && !user?.is_platform_owner) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchEvidence();
  }, [bookingId]);

  const fetchEvidence = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/bookings/${bookingId}/evidence`);
      setEvidence(response.data);
    } catch (error) {
      console.error('Error fetching evidence:', error);
      toast.error('Failed to load evidence');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-AU', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      in_progress: 'bg-purple-100 text-purple-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      disputed: 'bg-red-100 text-red-700'
    };
    return (
      <Badge className={statusColors[status] || 'bg-slate-100 text-slate-700'}>
        {status?.replace(/_/g, ' ').toUpperCase() || 'Unknown'}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#004B8D]" />
      </div>
    );
  }

  if (!evidence) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Evidence Not Found</h2>
            <p className="text-slate-500 mb-4">Could not load evidence for this booking.</p>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-lg font-semibold">Booking Evidence Review</h1>
              <p className="text-sm text-slate-500 font-mono">{bookingId}</p>
            </div>
          </div>
          <Link to={`/bookings/${bookingId}`}>
            <Button variant="outline" size="sm">
              <ExternalLink className="w-4 h-4 mr-2" />
              Full Booking
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Section 1: Booking Details */}
        <Card data-testid="booking-details-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#004B8D]" />
              Booking Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Status & Dates */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Status</span>
                  {getStatusBadge(evidence.booking?.status)}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Trip Dates</span>
                  <span className="text-sm font-medium">
                    {evidence.booking?.start_date} → {evidence.booking?.end_date}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Created</span>
                  <span className="text-sm">{formatDate(evidence.booking?.created_at)}</span>
                </div>
              </div>
              
              {/* Financial */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Total Price</span>
                  <span className="text-sm font-medium">${evidence.booking?.total_price?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Bond Amount</span>
                  <span className="text-sm font-medium">${evidence.booking?.bond_amount?.toFixed(2) || '0.00'}</span>
                </div>
              </div>
              
              {/* Vehicle */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Car className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium">
                    {evidence.vehicle?.make} {evidence.vehicle?.model} ({evidence.vehicle?.year})
                  </span>
                </div>
                <div className="text-sm text-slate-500">
                  Registration: {evidence.vehicle?.registration || 'N/A'}
                </div>
              </div>
            </div>

            {/* Parties */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-6 border-t border-slate-100">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">HOST</p>
                  <p className="font-medium">{evidence.host?.full_name || 'Unknown'}</p>
                  <p className="text-sm text-slate-500">{evidence.host?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">RENTER</p>
                  <p className="font-medium">{evidence.renter?.full_name || 'Unknown'}</p>
                  <p className="text-sm text-slate-500">{evidence.renter?.email}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Booking Agreement */}
        <Card data-testid="agreement-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#004B8D]" />
              Booking Agreement
            </CardTitle>
          </CardHeader>
          <CardContent>
            {evidence.agreement_url ? (
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-medium">Agreement PDF Available</p>
                    <p className="text-sm text-slate-500">Generated rental agreement between host and renter</p>
                  </div>
                </div>
                <Button onClick={() => window.open(evidence.agreement_url, '_blank')}>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
                <div>
                  <p className="font-medium">Agreement Not Generated</p>
                  <p className="text-sm text-slate-500">PDF agreement has not been generated for this booking</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 3: Pre-Hire Inspection Photos */}
        <Card data-testid="pre-inspection-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-[#004B8D]" />
                Pre-Hire Inspection
              </CardTitle>
              {evidence.pre_inspection?.submitted ? (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Submitted
                </Badge>
              ) : (
                <Badge variant="secondary">Not Submitted</Badge>
              )}
            </div>
            {evidence.pre_inspection?.submitted_at && (
              <CardDescription>
                Submitted: {formatDate(evidence.pre_inspection.submitted_at)}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {evidence.pre_inspection?.photos?.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {evidence.pre_inspection.photos.map((photo, index) => (
                  <div 
                    key={`pre-${index}`}
                    className="relative group cursor-pointer"
                    onClick={() => photo.url && setSelectedImage(photo.url)}
                  >
                    {photo.url ? (
                      <img 
                        src={photo.url} 
                        alt={`Pre-hire ${photo.section} ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-slate-200 group-hover:border-blue-400 transition-colors"
                      />
                    ) : (
                      <div className="w-full h-24 bg-slate-100 rounded-lg flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                    <span className="absolute bottom-1 left-1 text-xs bg-black/50 text-white px-1 rounded">
                      {photo.section?.replace(/_/g, ' ')}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Camera className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No pre-hire photos available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 4: Post-Hire Inspection Photos */}
        <Card data-testid="post-inspection-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-[#004B8D]" />
                Post-Hire Inspection
              </CardTitle>
              {evidence.post_inspection?.submitted ? (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Submitted
                </Badge>
              ) : (
                <Badge variant="secondary">Not Submitted</Badge>
              )}
            </div>
            {evidence.post_inspection?.submitted_at && (
              <CardDescription>
                Submitted: {formatDate(evidence.post_inspection.submitted_at)}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {evidence.post_inspection?.photos?.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {evidence.post_inspection.photos.map((photo, index) => (
                  <div 
                    key={`post-${index}`}
                    className="relative group cursor-pointer"
                    onClick={() => photo.url && setSelectedImage(photo.url)}
                  >
                    {photo.url ? (
                      <img 
                        src={photo.url} 
                        alt={`Post-hire ${photo.section} ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-slate-200 group-hover:border-blue-400 transition-colors"
                      />
                    ) : (
                      <div className="w-full h-24 bg-slate-100 rounded-lg flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                    <span className="absolute bottom-1 left-1 text-xs bg-black/50 text-white px-1 rounded">
                      {photo.section?.replace(/_/g, ' ')}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Camera className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No post-hire photos available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 5: Timeline */}
        <Card data-testid="timeline-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#004B8D]" />
              Booking Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            {evidence.timeline?.length > 0 ? (
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
                <div className="space-y-4">
                  {evidence.timeline.map((event, index) => (
                    <div key={index} className="flex items-start gap-4 ml-1">
                      <div className="w-7 h-7 rounded-full bg-white border-2 border-[#004B8D] flex items-center justify-center z-10">
                        <div className="w-2 h-2 rounded-full bg-[#004B8D]" />
                      </div>
                      <div className="flex-1 pb-4">
                        <p className="font-medium text-sm">{event.event}</p>
                        <p className="text-xs text-slate-500">{formatDate(event.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No timeline events available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Image Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <img 
            src={selectedImage} 
            alt="Full size"
            className="max-w-full max-h-full object-contain rounded-lg"
          />
          <button 
            className="absolute top-4 right-4 text-white hover:text-slate-300"
            onClick={() => setSelectedImage(null)}
          >
            <XCircle className="w-8 h-8" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminBookingEvidence;
